import { useMemo, useState } from "react";
import { Surgery, TissueOffer } from "../../../../models";
import { ColumnDef } from "@tanstack/react-table";
import { Button } from "react-bootstrap";
import { DataTable } from "../../../../component-library";
import { Link } from "react-router-dom";
import { Lists } from "../../../lists";
import { displayDate } from "../../../../helper-functions";
import { useSurgerySchedulerSurgeonQuery } from "../../../../queries";
import { TissueOfferModal } from "../../../tissue-offers";
import { usePermissions } from "../../../../contexts";
interface UpcomingSurgeriesProps {
    surgeries: Surgery[];
}

export function UpcomingSurgeries({ surgeries }: Readonly<UpcomingSurgeriesProps>) {
    const { isSurgeryUser } = usePermissions();
    const {
        mySurgeonsQuery: { data },
    } = useSurgerySchedulerSurgeonQuery({ queryType: "mySurgeons" });
    const [selectedOfferId, setSelectedOfferId] = useState<number>(0);
    const offers = surgeries
        .filter((s) => !!s.tissueOffers?.length)
        .flatMap((s) => s.tissueOffers)
        .filter((o) => !!o) as TissueOffer[];
    const selectedOffer = offers.find((o) => o.id === selectedOfferId);

    const showSurgeonColumn = !isSurgeryUser || (!!data && data.length > 1);

    const columns = useMemo<ColumnDef<Surgery>[]>(() => {
        const result: ColumnDef<Surgery>[] = [
            {
                accessorKey: "scheduledOnUtc",
                header: "Date/Time",
                cell: ({ row: { original: surgery } }) =>
                    surgery.scheduledOnUtc
                        ? new Date(surgery.scheduledOnUtc).toLocaleString("en-US", {
                              month: "2-digit",
                              day: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                          })
                        : "-",
            },
        ];

        if (showSurgeonColumn) {
            result.push({
                accessorKey: "surgeon.lastName",
                header: "Surgeon",
                cell: ({ row: { original: surgery } }) =>
                    surgery.surgeon?.lastName ? `${surgery.surgeon.lastName}, ${surgery.surgeon.firstName ?? ""}` : "-",
            });
        }

        result.push(
            ...([
                {
                    accessorKey: "recipientLastName",
                    header: "Patient",
                    cell: ({ row: { original: surgery } }) => {
                        return surgery.recipientLastName
                            ? `${surgery.recipientLastName}, ${surgery.recipientFirstName ?? ""}`
                            : "-";
                    },
                },
                {
                    accessorKey: "surgeryType.name",
                    header: "Surgery Type",
                    cell: ({ row: { original: surgery } }) => (surgery.surgeryType ? surgery.surgeryType.name : "-"),
                },
                {
                    accessorKey: "tissueId",
                    header: "Tissue",
                    cell: ({ row: { original: surgery } }) => {
                        if (surgery.tissue) return surgery.tissue.number;

                        const pendingOffer = surgery.tissueOffers?.find((o) => o.tissueOfferStatus?.name === "Offered");
                        if (pendingOffer)
                            return (
                                <Button size="sm" onClick={() => setSelectedOfferId(pendingOffer.id)}>
                                    View Offer
                                </Button>
                            );

                        return <span className="text-danger">UNFILLED</span>;
                    },
                },
                {
                    id: "actions",
                    header: "",
                    cell: ({ row: { original: surgery } }) => {
                        return (
                            <div className="text-end">
                                <Link
                                    to={`/lists/${Lists.SurgeriesForDate}/${displayDate(surgery.scheduledOnUtc)}/surgeries/${surgery.id}`}
                                    className="btn btn-sm btn-primary "
                                >
                                    View Surgery
                                </Link>
                            </div>
                        );
                    },
                },
            ] as ColumnDef<Surgery>[])
        );
        return result;
    }, [showSurgeonColumn]);

    return (
        <div>
            {surgeries.length ? (
                <DataTable columns={columns} data={surgeries} paginated={surgeries.length > 10} />
            ) : (
                <div>No surgeries found.</div>
            )}

            <TissueOfferModal
                show={!!selectedOfferId}
                onHide={() => setSelectedOfferId(0)}
                tissueOffer={selectedOffer}
            />
        </div>
    );
}
